<template>
  <div class="h5-carousel">
    <div class="h5-carousel-main">
      <div
        class="main-swiper"
        @click="onMainImageClick"
      >
        <Swiper
          @slide-change="onSlideChange"
          @swiper="setMainSwiper"
          effect="slide"
          grab-cursor
          centered-slides
          slides-per-view="1"
          :modules="[Thumbs, Virtual]"
          :center-insufficient-slides="true"
          :thumbs="{}"
          virtual
        >
          <SwiperSlide
            v-for="(image, index) in images"
            :key="index"
            :virtual-index="index"
          >
            <Image
              class="swiper-wrapper__image"
              :src="image"
              :alt="'Image ' + index"
              @loading-click.stop
            />
          </SwiperSlide>
          <div class="h5-offer-tool">
            <img
              src="https://cdn-image.bestdealer.com/app-bestdealer/1688/offer/offer-findsmilar-icon.svg"
              alt=""
              @click.stop="() => handleFind('similar', images[mainActiveIndex])"
            >
          </div>
        </Swiper>
        <div
          class="toggle-bar"
          :class="{ 'single-images': stylesImages.length === 0 }"
          @click.stop="handleToggleClick"
        >
          <div
            class="toggle-bar-transition"
            :style="{ left: isImages ? '2px' : 'calc(50% - 2px)' }"
          />
          <div class="toggle-bar-left">
            Images
          </div>
          <div
            v-if="stylesImages.length"
            class="toggle-bar-right"
          >
            Styles
          </div>
        </div>
        <div class="page-nums">
          <template v-if="isImages">
            {{ `${mainActiveIndex + 1}/${productImageLength}` }}
          </template>
          <template v-else>
            {{ `${activeIndex + 1}/${stylesImages.length}` }}
          </template>
        </div>
      </div>
    </div>
    <CarouselImagePreview
      v-model:show="showCarouselImagePreview"
      :images="images"
      :variant-images="variantImages"
      :products-length="productImageLength"
      :styles-length="stylesImages.length"
      :desc-images="descImages"
      :index="mainActiveIndex"
      @close="onImagePreviewClosed"
      @find-similar="(imageUrl) => {
        handleFind('similar', imageUrl)
        showCarouselImagePreview = false
      }"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { Thumbs, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/virtual'
import { buriedPointUtil } from '@/utils/utils'
import { useRoute, useRouter } from 'vue-router'
import { Image } from '@/components/Image'
import { useStore } from 'vuex'
import CarouselImagePreview from './CarouselImagePreview.vue'
import { useProductDetail } from '../../../hooks/useProductDetail'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  descImages: {
    type: Array,
    default: () => []
  }
})

const {
  productImages,
  swiperImages,
  styleImages: _stylesImages,
  variantImages,
  initSwiperImages
} = useProductDetail()

const router = useRouter()
const route = useRoute()

const store = useStore()
const images = ref([])
const currentIndex = ref(0)

const scrollThumbnailsContainer = ref(null)

const currentHoverdImage = computed(() => store.state.currentHoverdImage)

const emits = defineEmits(['change'])

const mainSwiper = ref(null) // 主图swiper实例

watch(() => props.descImages, (value) => {
  images.value = [...swiperImages.value, ...value]
})

// 绑定主图 Swiper 实例
const setMainSwiper = (swiper) => {
  if (swiper) {
    mainSwiper.value = swiper
  }
}

const stylesImages = ref([]) // 款式图集合
const productImageLength = ref(0) // 产品图数量

watch(currentIndex, (newIndex, oldIndex) => {
  scrollToIndex(newIndex)
})

watch(currentHoverdImage, (newData) => {
  if (newData) {
    const _index = images.value.findIndex(v => v === newData)
    if (_index !== -1) {
      currentIndex.value = _index
      currentHoverdImage.value = newData
    }
  }
})

const activeIndex = ref(-1) // -1: 默认显示主图，从0开始表示缩略图索引。
const mainActiveIndex = ref(0) // 主图索引

const showCarouselImagePreview = ref(false)
const onMainImageClick = () => {
  showCarouselImagePreview.value = true
}

const onImagePreviewClosed = (index) => {
  mainSwiper.value.slideTo(index, 0)
  mainActiveIndex.value = index
}

const onSlideChange = (swiper) => {
  const realIndex = swiper.activeIndex
  const onlyMainImages = stylesImages.value.length === 0
  mainActiveIndex.value = realIndex
  if (onlyMainImages) {
    mainSwiper.value.slideTo(mainActiveIndex.value)
    return
  };

  if (realIndex > productImageLength.value - 1) {
    // 微缩轮播图激活的坐标
    const _index = realIndex - productImageLength.value
    activeIndex.value = _index
    isImages.value = false
  } else {
    isImages.value = true
    activeIndex.value = -1
  }

  emits('change', activeIndex.value)
}

const isImages = ref(true)
const handleToggleClick = () => {
  isImages.value = !isImages.value

  if (isImages.value) {
    activeIndex.value = -1
    mainSwiper.value.slideTo(0, 0)
  } else {
    activeIndex.value = 0
    const firstStyleImageIndex = productImageLength.value
    mainSwiper.value.slideTo(firstStyleImageIndex, 0)
  }
}

// 滚动轮播图
const scrollToIndex = (index) => {
  const scrollThumbnailsContainerEle = scrollThumbnailsContainer.value
  const items = scrollThumbnailsContainerEle.children // 所有子元素
  const itemWidth = items[0].offsetWidth // 单个图片的宽度（含间距）
  const containerWidth = scrollThumbnailsContainerEle.clientWidth // 容器的可见宽度
  const currentItem = items[index] // 当前索引对应的元素
  const itemLeft = currentItem.offsetLeft // 当前块的左侧距离容器的左边缘距离
  const itemRight = itemLeft + itemWidth // 当前块的右边界距离容器的左边缘距离

  // 判断是否需要滚动
  const isOutOfView = itemLeft < scrollThumbnailsContainerEle.scrollLeft ||
    itemRight > scrollThumbnailsContainerEle.scrollLeft + containerWidth

  if (isOutOfView) {
    // 将当前块移动到容器的中间位置
    const targetScroll = itemLeft - (containerWidth - itemWidth) / 2
    scrollThumbnailsContainerEle.scrollTo({
      left: targetScroll,
      behavior: 'smooth' // 平滑滚动
    })
  }
}

const handleFind = (type, imageUrl) => {
  if (type === 'similar') {
    buriedPointUtil({ eventId: 'MPOP201' })
  } else {
    buriedPointUtil({ eventId: 'MPOP202' })
  }

  sessionStorage.setItem(`currentMainImage_${props.data.offerId}`, imageUrl)

  nextTick(() => {
    const url = `/search?offerId=${props.data.offerId}&type=${type}`
    if (route.fullPath.includes('imageId')) {
      router.replace(url)
    } else {
      router.push(url)
    }
  })
}

onMounted(() => {
  initSwiperImages(props.data)
  images.value = swiperImages.value
  stylesImages.value = _stylesImages.value
  productImageLength.value = productImages.value.length
})
</script>

<style lang="less">
@import './index.less';
</style>
